/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================
*/

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Divider from "@mui/material/Divider";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import AppLoader from "components/AppLoader";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Data
import membersTableData from "layouts/tables/data/membersTableData";
import fetchWithAuth from "layouts/tables/data/api";

// API Configuration
import appSettings from "appsettings.json";

import { useEffect, useState } from "react";

function Members() {
  const [members, setMembers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openAssignRole, setOpenAssignRole] = useState(false);
  const [openStatusConfirm, setOpenStatusConfirm] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedStatusMember, setSelectedStatusMember] = useState(null);
  const [isRoleActive, setIsRoleActive] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [statusUpdatingMemberId, setStatusUpdatingMemberId] = useState(null);
  const [assignedRoles, setAssignedRoles] = useState([]);
  const [unassignedRoles, setUnassignedRoles] = useState([]);

  const normalizeArray = (value) => {
    if (Array.isArray(value)) return value;
    if (value && Array.isArray(value.$values)) return value.$values;
    return [];
  };

  const resolveMemberId = (member) =>
    member?.memberId ??
    member?.MemberId ??
    member?.memberID ??
    member?.MemberID ??
    member?.id ??
    member?.Id ??
    null;

  const buildGetAllUsersUrl = () => {
    const base = `${appSettings.baseUrl}${appSettings.endpoints.getAllUsers}`;
    const separator = base.includes("?") ? "&" : "?";
    return `${base}${separator}_ts=${Date.now()}`;
  };

  const fetchMembers = async () => {
    setLoading(true);
    setError(null);
    const url = buildGetAllUsersUrl();
    try {
      const response = await fetchWithAuth(url, {
        method: "GET",
        cache: "no-store",
      });
      if (!response.ok) throw new Error("An error occurred while fetching members.");
      const data = await response.json();
      const normalized = data?.$values ?? (Array.isArray(data) ? data : []);
      setMembers(normalized);
      return normalized;
    } catch (err) {
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const resolveLatestMemberStatus = (member) => {
    const memberId = member?.memberId ?? member?.MemberId ?? member?.id ?? member?.Id ?? null;
    if (!memberId) return false;

    const latestMember = members.find((m) => String(getMemberId(m)) === String(memberId));
    if (latestMember) {
      return latestMember?.isActive ?? latestMember?.IsActive ?? false;
    }

    return member?.isActive ?? member?.IsActive ?? false;
  };

  const resolveBooleanStatus = (value, fallback) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      if (normalized === "1" || normalized === "true") return true;
      if (normalized === "0" || normalized === "false") return false;
    }
    return fallback;
  };

  const fetchRoles = () => {
    const url = `${appSettings.baseUrl}${appSettings.endpoints.getAllRoles}`;
    fetchWithAuth(url)
      .then((response) => {
        if (!response.ok) throw new Error("An error occurred while fetching roles.");
        return response.json();
      })
      .then((data) => {
        const rolesData = normalizeArray(data);
        setRoles(rolesData);
      })
      .catch((err) => setError(err.message));
  };

  useEffect(() => {
    fetchMembers();
    fetchRoles();
  }, []);

  useEffect(() => {
    if (!openAssignRole || !selectedMember) return;
    const { assigned, unassigned } = buildRoleLists(selectedMember, roles);
    setAssignedRoles(assigned);
    setUnassignedRoles(unassigned);
  }, [roles, openAssignRole, selectedMember]);

  const getRoleId = (role) => role?.roleId ?? role?.RoleId ?? role?.id ?? role?.Id ?? null;
  const getRoleName = (role) => role?.name ?? role?.Name ?? `Role ${getRoleId(role)}`;
  const getMemberId = (member) => resolveMemberId(member);

  const refreshSelectedMemberAndLists = async (memberId) => {
    const url = buildGetAllUsersUrl();
    const response = await fetchWithAuth(url, {
      method: "GET",
      cache: "no-store",
    });
    if (!response.ok) return;

    const data = await response.json();
    const normalized = data?.$values ?? (Array.isArray(data) ? data : []);
    const updatedMember = normalized.find((m) => {
      const id = resolveMemberId(m);
      return String(id) === String(memberId);
    });

    if (!updatedMember) return;

    const transformedMember = {
      memberId: resolveMemberId(updatedMember),
      name: updatedMember?.name ?? updatedMember?.Name ?? "",
      email: updatedMember?.email ?? updatedMember?.Email ?? "",
      roleNames: normalizeArray(updatedMember?.roleNames ?? updatedMember?.RoleNames),
      memberRoles: normalizeArray(updatedMember?.memberRoles ?? updatedMember?.MemberRoles),
    };

    setSelectedMember(transformedMember);
    const { assigned, unassigned } = buildRoleLists(transformedMember, roles);
    setAssignedRoles(assigned);
    setUnassignedRoles(unassigned);
  };

  const buildRoleLists = (member, allRoles) => {
    const normalizedMemberRoles = normalizeArray(member?.memberRoles ?? member?.MemberRoles).map(
      (mr) => ({
        memberRoleId: mr?.memberRoleId ?? mr?.MemberRoleId ?? mr?.id ?? mr?.Id ?? null,
        roleId: mr?.roleId ?? mr?.RoleId ?? mr?.role?.roleId ?? mr?.Role?.RoleId ?? null,
        roleName: mr?.roleName ?? mr?.RoleName ?? mr?.role?.name ?? mr?.Role?.Name ?? "",
      })
    );

    const roleNames = normalizeArray(member?.roleNames ?? member?.RoleNames).map((r) =>
      String(r).toLowerCase()
    );
    const roleNameSet = new Set(roleNames);

    const assigned = normalizeArray(allRoles)
      .filter((role) => {
        const roleId = getRoleId(role);
        const roleName = getRoleName(role);
        return (
          normalizedMemberRoles.some(
            (mr) =>
              (mr.roleId !== null && roleId !== null && String(mr.roleId) === String(roleId)) ||
              (mr.roleName && roleName && mr.roleName.toLowerCase() === roleName.toLowerCase())
          ) || roleNameSet.has(String(roleName).toLowerCase())
        );
      })
      .map((role) => {
        const roleId = getRoleId(role);
        const roleName = getRoleName(role);
        const existingMemberRole = normalizedMemberRoles.find(
          (mr) =>
            (mr.roleId !== null && roleId !== null && String(mr.roleId) === String(roleId)) ||
            (mr.roleName && roleName && mr.roleName.toLowerCase() === roleName.toLowerCase())
        );

        return {
          roleId,
          roleName,
          memberRoleId: existingMemberRole?.memberRoleId ?? null,
        };
      });

    const assignedFromMemberOnly = normalizedMemberRoles
      .filter(
        (mr) =>
          !assigned.some(
            (ar) =>
              (ar.roleId !== null &&
                mr.roleId !== null &&
                String(ar.roleId) === String(mr.roleId)) ||
              (ar.roleName &&
                mr.roleName &&
                ar.roleName.toLowerCase() === mr.roleName.toLowerCase())
          )
      )
      .map((mr) => ({
        roleId: mr.roleId ?? null,
        roleName: mr.roleName || `Role ${mr.roleId ?? ""}`.trim(),
        memberRoleId: mr.memberRoleId ?? null,
      }));

    const assignedCombined = [...assigned, ...assignedFromMemberOnly];

    const assignedByIdSet = new Set(
      assignedCombined.filter((r) => r.roleId !== null).map((r) => String(r.roleId))
    );
    const assignedByNameSet = new Set(
      assignedCombined.map((r) => String(r.roleName).toLowerCase())
    );

    const unassigned = normalizeArray(allRoles)
      .filter((role) => (role.isActive ?? role.IsActive ?? true) === true)
      .filter((role) => {
        const roleId = getRoleId(role);
        const roleName = getRoleName(role);
        return (
          !assignedByIdSet.has(String(roleId)) &&
          !assignedByNameSet.has(String(roleName).toLowerCase())
        );
      })
      .map((role) => ({
        roleId: getRoleId(role),
        roleName: getRoleName(role),
      }));

    return { assigned: assignedCombined, unassigned };
  };

  const updateMemberInGridLocal = ({ memberId, roleId, roleName, memberRoleId, action }) => {
    setMembers((prev) =>
      prev.map((member) => {
        if (String(getMemberId(member)) !== String(memberId)) return member;

        const currentRoleNames = normalizeArray(member?.roleNames ?? member?.RoleNames);
        const currentMemberRoles = normalizeArray(member?.memberRoles ?? member?.MemberRoles);

        if (action === "assign") {
          const nextRoleNames = currentRoleNames.includes(roleName)
            ? currentRoleNames
            : [...currentRoleNames, roleName];

          const roleAlreadyExists = currentMemberRoles.some(
            (mr) =>
              String(mr?.roleId ?? mr?.RoleId ?? mr?.role?.roleId ?? mr?.Role?.RoleId ?? "") ===
                String(roleId) ||
              String(mr?.roleName ?? mr?.RoleName ?? mr?.role?.name ?? mr?.Role?.Name ?? "") ===
                String(roleName)
          );

          const nextMemberRoles = roleAlreadyExists
            ? currentMemberRoles
            : [...currentMemberRoles, { memberRoleId, roleId, roleName }];

          return {
            ...member,
            roleNames: nextRoleNames,
            memberRoles: nextMemberRoles,
          };
        }

        const nextRoleNames = currentRoleNames.filter((name) => String(name) !== String(roleName));
        const nextMemberRoles = currentMemberRoles.filter((mr) => {
          if (memberRoleId) {
            return (
              String(mr?.memberRoleId ?? mr?.MemberRoleId ?? mr?.id ?? mr?.Id ?? "") !==
              String(memberRoleId)
            );
          }

          return (
            String(mr?.roleId ?? mr?.RoleId ?? mr?.role?.roleId ?? mr?.Role?.RoleId ?? "") !==
              String(roleId) &&
            String(mr?.roleName ?? mr?.RoleName ?? mr?.role?.name ?? mr?.Role?.Name ?? "") !==
              String(roleName)
          );
        });

        return {
          ...member,
          roleNames: nextRoleNames,
          memberRoles: nextMemberRoles,
        };
      })
    );
  };

  const handleOpenAssignRole = (member) => {
    setSelectedMember(member);
    setIsRoleActive(true);
    const { assigned, unassigned } = buildRoleLists(member, roles);
    setAssignedRoles(assigned);
    setUnassignedRoles(unassigned);
    setOpenAssignRole(true);
  };

  const handleCloseAssignRole = () => {
    if (assigning) return;
    setOpenAssignRole(false);
    setSelectedMember(null);
    setAssignedRoles([]);
    setUnassignedRoles([]);
    setIsRoleActive(true);
  };

  const handleAssignRole = async (role) => {
    const memberId = resolveMemberId(selectedMember);

    if (!memberId) {
      setError("Unable to assign role: member id is missing.");
      return;
    }

    if (!role?.roleId) {
      setError("Please select a role before assigning.");
      return;
    }

    setAssigning(true);
    setError(null);

    const url = `${appSettings.baseUrl}${
      appSettings.endpoints.createMemberRole
    }?roleId=${encodeURIComponent(role.roleId)}&memberId=${encodeURIComponent(memberId)}&isActive=${
      isRoleActive ? 1 : 0
    }`;

    try {
      const response = await fetchWithAuth(url, { method: "POST" });
      if (!response.ok) {
        let errorMessage = "An error occurred while assigning role.";
        try {
          const errorData = await response.json();
          errorMessage = errorData?.title || errorData?.message || errorData?.error || errorMessage;
        } catch (e) {
          // Keep default error text when API doesn't return JSON.
        }
        throw new Error(errorMessage);
      }

      let createdMemberRole = null;
      try {
        createdMemberRole = await response.json();
      } catch (e) {
        // Keep UI fallback when API doesn't return JSON body.
      }

      const createdMemberRoleId =
        createdMemberRole?.memberRoleId ??
        createdMemberRole?.MemberRoleId ??
        createdMemberRole?.id ??
        createdMemberRole?.Id ??
        null;

      setUnassignedRoles((prev) => prev.filter((r) => String(r.roleId) !== String(role.roleId)));
      setAssignedRoles((prev) => {
        const alreadyAdded = prev.some((r) => String(r.roleId) === String(role.roleId));
        if (alreadyAdded) return prev;
        return [
          ...prev,
          {
            roleId: role.roleId,
            roleName: role.roleName,
            memberRoleId: createdMemberRoleId,
          },
        ];
      });

      setSelectedMember((prev) => {
        if (!prev) return prev;
        const prevMemberRoles = normalizeArray(prev.memberRoles ?? prev.MemberRoles);
        const prevRoleNames = normalizeArray(prev.roleNames ?? prev.RoleNames);

        const nextMemberRoles = prevMemberRoles.some(
          (mr) =>
            String(mr?.roleId ?? mr?.RoleId ?? mr?.role?.roleId ?? mr?.Role?.RoleId ?? "") ===
            String(role.roleId)
        )
          ? prevMemberRoles
          : [
              ...prevMemberRoles,
              {
                memberRoleId: createdMemberRoleId,
                roleId: role.roleId,
                roleName: role.roleName,
              },
            ];

        const nextRoleNames = prevRoleNames.includes(role.roleName)
          ? prevRoleNames
          : [...prevRoleNames, role.roleName];

        return {
          ...prev,
          memberRoles: nextMemberRoles,
          roleNames: nextRoleNames,
        };
      });

      updateMemberInGridLocal({
        memberId,
        roleId: role.roleId,
        roleName: role.roleName,
        memberRoleId: createdMemberRoleId,
        action: "assign",
      });

      fetchMembers();
      refreshSelectedMemberAndLists(memberId).catch(() => {});
    } catch (err) {
      setError(err.message);
    } finally {
      setAssigning(false);
    }
  };

  const handleRemoveRole = async (role) => {
    if (!role?.memberRoleId) {
      setError("Unable to remove role: assignment id is missing.");
      return;
    }

    const memberId = resolveMemberId(selectedMember);

    if (!memberId) {
      setError("Unable to remove role: member id is missing.");
      return;
    }

    setAssigning(true);
    setError(null);

    const formBody = new URLSearchParams();
    formBody.append("id", String(role.memberRoleId));
    const url = `${appSettings.baseUrl}${appSettings.endpoints.deleteMemberRole}`;

    try {
      const response = await fetchWithAuth(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formBody.toString(),
      });

      if (!response.ok) {
        let errorMessage = "An error occurred while removing role.";
        try {
          const errorData = await response.json();
          errorMessage = errorData?.title || errorData?.message || errorData?.error || errorMessage;
        } catch (e) {
          // Keep default error message when response body is not JSON.
        }
        throw new Error(errorMessage);
      }

      setAssignedRoles((prev) =>
        prev.filter((r) => String(r.roleId ?? r.roleName) !== String(role.roleId ?? role.roleName))
      );
      setUnassignedRoles((prev) => {
        const exists = prev.some((r) => String(r.roleId) === String(role.roleId));
        if (exists) return prev;
        return [...prev, { roleId: role.roleId, roleName: role.roleName }];
      });

      setSelectedMember((prev) => {
        if (!prev) return prev;
        const prevMemberRoles = normalizeArray(prev.memberRoles ?? prev.MemberRoles);
        const prevRoleNames = normalizeArray(prev.roleNames ?? prev.RoleNames);

        const nextMemberRoles = prevMemberRoles.filter(
          (mr) =>
            String(mr?.memberRoleId ?? mr?.MemberRoleId ?? mr?.id ?? mr?.Id ?? "") !==
            String(role.memberRoleId)
        );

        const nextRoleNames = prevRoleNames.filter(
          (name) => String(name) !== String(role.roleName)
        );

        return {
          ...prev,
          memberRoles: nextMemberRoles,
          roleNames: nextRoleNames,
        };
      });

      updateMemberInGridLocal({
        memberId,
        roleId: role.roleId,
        roleName: role.roleName,
        memberRoleId: role.memberRoleId,
        action: "remove",
      });

      fetchMembers();
      refreshSelectedMemberAndLists(memberId).catch(() => {});
    } catch (err) {
      setError(err.message);
    } finally {
      setAssigning(false);
    }
  };

  const handleToggleMemberStatus = async (member) => {
    const memberId = resolveMemberId(member);

    if (!memberId) {
      setError("Unable to update member status: member id is missing.");
      return;
    }

    const currentIsActive = resolveLatestMemberStatus(member);
    const nextIsActive = currentIsActive ? 0 : 1;

    setStatusUpdatingMemberId(memberId);
    setError(null);

    const url = `${appSettings.baseUrl}${appSettings.endpoints.toggleMemberStatus}`;
    const formBody = new URLSearchParams();
    formBody.append("memberId", String(memberId));
    formBody.append("isActive", String(nextIsActive));

    try {
      const response = await fetchWithAuth(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formBody.toString(),
      });
      if (!response.ok) {
        let errorMessage = "An error occurred while updating member status.";
        try {
          const errorData = await response.json();
          errorMessage = errorData?.title || errorData?.message || errorData?.error || errorMessage;
        } catch (e) {
          // Keep default error text when API doesn't return JSON.
        }
        throw new Error(errorMessage);
      }

      let responseData = null;
      try {
        responseData = await response.json();
      } catch (e) {
        // Keep fallback behavior when API response body is empty.
      }

      const persistedIsActive = resolveBooleanStatus(responseData?.isActive, !!nextIsActive);

      setMembers((prev) =>
        prev.map((item) => {
          const itemMemberId = item?.memberId ?? item?.MemberId ?? item?.id ?? item?.Id ?? null;
          if (String(itemMemberId) !== String(memberId)) return item;
          return { ...item, isActive: persistedIsActive, IsActive: persistedIsActive };
        })
      );

      setSelectedMember((prev) => {
        if (!prev) return prev;
        const selectedMemberId = prev?.memberId ?? prev?.MemberId ?? prev?.id ?? prev?.Id ?? null;
        if (String(selectedMemberId) !== String(memberId)) return prev;
        return { ...prev, isActive: persistedIsActive, IsActive: persistedIsActive };
      });

      await fetchMembers();
    } catch (err) {
      setError(err.message);
    } finally {
      setStatusUpdatingMemberId(null);
    }
  };

  const handleOpenStatusConfirm = (member) => {
    const memberId = resolveMemberId(member);
    if (!memberId) {
      setError("Unable to update member status: member id is missing.");
      return;
    }
    const latestMember = members.find((m) => String(getMemberId(m)) === String(memberId));
    setSelectedStatusMember(latestMember ?? member);
    setOpenStatusConfirm(true);
  };

  const handleCloseStatusConfirm = () => {
    if (statusUpdatingMemberId !== null) return;
    setOpenStatusConfirm(false);
    setSelectedStatusMember(null);
  };

  const handleConfirmToggleStatus = async () => {
    if (!selectedStatusMember) return;
    await handleToggleMemberStatus(selectedStatusMember);
    setOpenStatusConfirm(false);
    setSelectedStatusMember(null);
  };

  const handleDragStart = (event, role, source) => {
    event.dataTransfer.setData(
      "text/plain",
      JSON.stringify({
        source,
        roleId: role.roleId,
        roleName: role.roleName,
      })
    );
  };

  const handleDropOnAssigned = async (event) => {
    event.preventDefault();
    if (assigning) return;

    let payload;
    try {
      payload = JSON.parse(event.dataTransfer.getData("text/plain"));
    } catch {
      return;
    }

    if (!payload || payload.source !== "unassigned") return;

    const role = unassignedRoles.find((r) => String(r.roleId) === String(payload.roleId));
    if (!role) return;

    await handleAssignRole(role);
  };

  const handleDropOnUnassigned = async (event) => {
    event.preventDefault();
    if (assigning) return;

    let payload;
    try {
      payload = JSON.parse(event.dataTransfer.getData("text/plain"));
    } catch {
      return;
    }

    if (!payload || payload.source !== "assigned") return;

    const role = assignedRoles.find(
      (r) => String(r.roleId ?? r.roleName) === String(payload.roleId ?? payload.roleName)
    );
    if (!role) return;

    await handleRemoveRole(role);
  };

  const { columns, rows } = membersTableData(
    members,
    handleOpenAssignRole,
    handleOpenStatusConfirm,
    statusUpdatingMemberId
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={2}
                px={3}
                variant="gradient"
                bgColor="success"
                borderRadius="lg"
                coloredShadow="success"
              >
                <MDTypography variant="h6" color="white" fontWeight="medium">
                  Members
                </MDTypography>
                <MDTypography variant="caption" color="white" opacity={0.85}>
                  Manage users and role assignments
                </MDTypography>
              </MDBox>
              <MDBox px={3} pt={3} pb={2}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" alignItems="center" py={10}>
                    <AppLoader />
                  </MDBox>
                ) : error ? (
                  <MDBox p={3}>
                    <MDTypography variant="button" color="error">
                      Error: {error}
                    </MDTypography>
                  </MDBox>
                ) : (
                  <DataTable
                    table={{ columns, rows }}
                    canSearch
                    isSorted={true}
                    entriesPerPage={{ defaultValue: 5, entries: [5, 10, 15, 20] }}
                    showTotalEntries={true}
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      <Dialog open={openAssignRole} onClose={handleCloseAssignRole} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pb: 1 }}>
          <MDTypography variant="h6" fontWeight="medium">
            Role Assignment
          </MDTypography>
        </DialogTitle>
        <DialogContent>
          <MDBox display="flex" flexDirection="column" pt={1} pb={1}>
            <MDBox
              mb={2}
              px={2}
              py={1.5}
              borderRadius="lg"
              bgColor="light"
              border="1px solid"
              borderColor="light"
            >
              <MDTypography variant="button" color="dark" fontWeight="bold">
                Member: {selectedMember?.name || selectedMember?.Name || "-"}
              </MDTypography>
              <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                {selectedMember?.email || selectedMember?.Email || ""}
              </MDTypography>
            </MDBox>
            <Divider />
            <MDBox mb={2}>
              <MDBox
                display="grid"
                gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }}
                gap={2}
                mt={2}
              >
                <MDBox
                  p={1.5}
                  border="1px solid"
                  borderColor="secondary"
                  borderRadius="lg"
                  minHeight="220px"
                  bgColor="light"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleDropOnUnassigned}
                >
                  <MDTypography variant="button" color="secondary" fontWeight="bold" mb={1.5}>
                    Unassigned Roles
                  </MDTypography>
                  <MDBox display="flex" flexDirection="column" gap={1}>
                    {unassignedRoles.map((role) => (
                      <MDBox
                        key={`unassigned-${role.roleId}`}
                        p={1}
                        border="1px solid"
                        borderColor="secondary"
                        borderRadius="md"
                        bgColor="light"
                        draggable={!assigning}
                        onDragStart={(e) => handleDragStart(e, role, "unassigned")}
                        sx={{
                          cursor: assigning ? "not-allowed" : "grab",
                          transition: "all 0.15s ease-in-out",
                          "&:hover": {
                            backgroundColor: "#eceff1",
                          },
                        }}
                      >
                        <MDTypography variant="caption" fontWeight="medium">
                          {role.roleName}
                        </MDTypography>
                      </MDBox>
                    ))}
                    {unassignedRoles.length === 0 && (
                      <MDTypography variant="caption" color="text" opacity={0.8}>
                        No roles available
                      </MDTypography>
                    )}
                  </MDBox>
                </MDBox>

                <MDBox
                  p={1.5}
                  border="1px solid"
                  borderColor="success"
                  borderRadius="lg"
                  minHeight="220px"
                  sx={{ backgroundColor: "rgba(76, 175, 80, 0.08)" }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleDropOnAssigned}
                >
                  <MDTypography variant="button" color="success" fontWeight="bold" mb={1.5}>
                    Assigned Roles
                  </MDTypography>
                  <MDBox display="flex" flexDirection="column" gap={1}>
                    {assignedRoles.map((role) => (
                      <MDBox
                        key={`assigned-${role.roleId ?? role.roleName}`}
                        p={1}
                        border="1px solid"
                        borderColor="success"
                        borderRadius="md"
                        draggable={!assigning}
                        onDragStart={(e) => handleDragStart(e, role, "assigned")}
                        sx={{
                          backgroundColor: "rgba(76, 175, 80, 0.16)",
                          cursor: assigning ? "not-allowed" : "grab",
                          transition: "all 0.15s ease-in-out",
                          "&:hover": {
                            backgroundColor: "rgba(76, 175, 80, 0.24)",
                          },
                        }}
                      >
                        <MDTypography variant="caption" fontWeight="medium">
                          {role.roleName}
                        </MDTypography>
                      </MDBox>
                    ))}
                    {assignedRoles.length === 0 && (
                      <MDTypography variant="caption" color="text" opacity={0.8}>
                        No assigned roles
                      </MDTypography>
                    )}
                  </MDBox>
                </MDBox>
              </MDBox>
            </MDBox>
            <FormControlLabel
              sx={{ mt: 0.5, ml: 0.5 }}
              control={
                <Switch
                  checked={isRoleActive}
                  onChange={(e) => setIsRoleActive(e.target.checked)}
                />
              }
              label="Assign as active"
            />
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton
            onClick={handleCloseAssignRole}
            color="secondary"
            variant="outlined"
            disabled={assigning}
          >
            Cancel
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={openStatusConfirm} onClose={handleCloseStatusConfirm} fullWidth maxWidth="xs">
        <DialogTitle>Confirm Status Change</DialogTitle>
        <DialogContent>
          <MDTypography variant="button" color="text">
            Are you sure you want to{" "}
            {selectedStatusMember?.isActive ?? selectedStatusMember?.IsActive ?? false
              ? "deactivate"
              : "activate"}{" "}
            this user?
          </MDTypography>
          {selectedStatusMember && (
            <MDBox mt={1}>
              <MDTypography variant="caption" color="text" fontWeight="medium">
                {selectedStatusMember?.name ?? selectedStatusMember?.Name ?? "-"}
              </MDTypography>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton
            onClick={handleCloseStatusConfirm}
            color="secondary"
            disabled={statusUpdatingMemberId !== null}
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={handleConfirmToggleStatus}
            variant="gradient"
            color={
              selectedStatusMember?.isActive ?? selectedStatusMember?.IsActive ?? false
                ? "error"
                : "success"
            }
            disabled={statusUpdatingMemberId !== null}
          >
            Confirm
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default Members;
